import { QueryCache, type VueQueryPluginOptions } from '@tanstack/vue-query';
import axios from 'axios';
import * as Sentry from '@sentry/vue';
import type { UserPayload } from '@/composables/api/users/types';
import type { GetApiParcelFinderParams } from '@/composables/api/parcelFinder/types';
import type {
  GetApiActivitiesParams,
  GetApiActivitiesRttParams,
  GetApiActivitiesSummaryParams,
  GetApiTourTuningRecommendationsParams
} from '@/composables/api/usageAnalysis/types';

export const vueQueryOptions: VueQueryPluginOptions = {
  enableDevtoolsV6Plugin: true,
  queryClientConfig: {
    queryCache: new QueryCache({
      onError: (error, query) => {
        console.error(`[Global Query Error]: ${error.message}`);

        // Handle Axios-specific errors
        if (axios.isAxiosError(error)) {
          // 1. FILTER: Ignore cancellations (user navigating away)
          // This stops the "Network Error" on navigation from flooding Sentry
          if (axios.isCancel(error)) return;

          // 2. FILTER: Ignore status 0 (often related to navigation or lost connection)
          // If it's a persistent issue, Sentry will likely catch it elsewhere
          if (axios.isAxiosError(error) && error.response?.status === 0) return;

          // 3. Capture everything else to Sentry
          Sentry.captureException(error, {
            extra: {
              queryKey: query.queryKey,
              // Include helpful metadata for debugging
              url: error.config?.url,
              method: error.config?.method,
              status: error.response?.status,
              isOnline: navigator.onLine
            },
            // Tag it so you can filter in the Sentry Dashboard
            tags: {
              category: 'api-query',
              layer: 'tanstack-query'
            }
          });
        }

        // Handle non-Axios errors (General JS crashes)
        Sentry.captureException(error, {
          extra: { queryKey: query.queryKey }
        });

        // @TODO: Trigger a UI notification for 500s
        // Example: Global snackbar notification
        if (
          axios.isAxiosError(error) &&
          error.response &&
          error.response.status >= 500
        ) {
          // triggerGlobalSnackbar(t('errors.server_error'));
        }
      }
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 2,
        // Using a clear variable name for the 2-minute stale time
        staleTime: 1000 * 60 * 2,
        // Ensures data stays in cache for 5 mins even if not being used
        gcTime: 1000 * 60 * 5
      }
    }
  }
};

/** * Centralized Query Keys
 * Prevents typos like ['widget', 'todo'] vs ['widgets', 'todos']
 */
export const queryKeys = {
  liveStatus: (internalDepotId: number, subcontractorId?: string) =>
    ['liveStatus', internalDepotId, subcontractorId] as const,

  users: {
    all: ['users'] as const,
    // Base key for all user lists
    lists: () => [...queryKeys.users.all, 'list'] as const,
    // Granular key for a specific search/filter/page combination
    filteredLists: (payload: UserPayload) =>
      [...queryKeys.users.lists(), payload] as const
  },

  parcelFinder: (payload: GetApiParcelFinderParams) =>
    ['parcelFinder', payload] as const,

  widgets: {
    all: ['widgets'] as const,
    todos: (internalDepotId: string) =>
      [...queryKeys.widgets.all, 'todos', internalDepotId] as const,
    rtt: (internalDepotId: string) =>
      [...queryKeys.widgets.all, 'rtt', internalDepotId] as const,
    weather: (internalDepotId: string, days: number) =>
      [...queryKeys.widgets.all, 'weather', internalDepotId, days] as const
  },

  usageAnalysis: {
    all: ['usageAnalysis'] as const,
    bars: (payload: GetApiActivitiesSummaryParams) =>
      [...queryKeys.usageAnalysis.all, 'bars', payload] as const,
    activities: (payload: GetApiActivitiesParams) =>
      [...queryKeys.usageAnalysis.all, 'activities', payload] as const,
    rtt: (payload: GetApiActivitiesRttParams) =>
      [...queryKeys.usageAnalysis.all, 'rtt', payload] as const,
    tourRecommendations: (payload: GetApiTourTuningRecommendationsParams) =>
      [...queryKeys.usageAnalysis.all, 'tourRecommendations', payload] as const
  }
};
